import { z } from "zod";
import { BusinessGraphV1Schema } from "./business-graph.js";
import { BusinessGraphProvenanceDomainV1Schema } from "./business-graph-provenance.js";
import { assertBusinessGraphWebsiteSafe, BUSINESS_GRAPH_FORBIDDEN_TOP_LEVEL_KEYS, parseBusinessGraphWebsiteSafe, } from "./business-graph-website-safe.js";
export const BUSINESS_GRAPH_VERIFIED_TRUTH_EXPORT_SCHEMA_VERSION = "businessgraph-verified-truth-export.v1.0";
export class BusinessGraphVerifiedTruthImportError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "BusinessGraphVerifiedTruthImportError";
        this.code = code;
    }
}
export const BusinessGraphVerifiedTruthExportV1Schema = z
    .object({
    schemaVersion: z.literal(BUSINESS_GRAPH_VERIFIED_TRUTH_EXPORT_SCHEMA_VERSION),
    exportedAt: z.string().datetime(),
    graph: BusinessGraphV1Schema,
    provenance: BusinessGraphProvenanceDomainV1Schema,
})
    .strict();
const FORBIDDEN_TOP_LEVEL = new Set(BUSINESS_GRAPH_FORBIDDEN_TOP_LEVEL_KEYS);
function isPlainObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
/** True when any object in the JSON tree uses a forbidden Run/ClientOps key. */
export function businessGraphPayloadContainsForbiddenKeys(value) {
    if (Array.isArray(value)) {
        return value.some((item) => businessGraphPayloadContainsForbiddenKeys(item));
    }
    if (!isPlainObject(value)) {
        return false;
    }
    for (const [key, child] of Object.entries(value)) {
        if (FORBIDDEN_TOP_LEVEL.has(key)) {
            return true;
        }
        if (businessGraphPayloadContainsForbiddenKeys(child)) {
            return true;
        }
    }
    return false;
}
/**
 * Versioned export of website-safe verified BusinessGraph truth plus provenance records.
 */
export function exportBusinessGraphVerifiedTruth(graph, options) {
    const safe = assertBusinessGraphWebsiteSafe(graph);
    return BusinessGraphVerifiedTruthExportV1Schema.parse({
        schemaVersion: BUSINESS_GRAPH_VERIFIED_TRUTH_EXPORT_SCHEMA_VERSION,
        exportedAt: options?.exportedAt ?? new Date().toISOString(),
        graph: safe,
        provenance: safe.provenance,
    });
}
/**
 * Safe re-import: rejects forbidden private keys, validates envelope schema, returns strict graph.
 */
export function importBusinessGraphVerifiedTruth(payload) {
    if (businessGraphPayloadContainsForbiddenKeys(payload)) {
        throw new BusinessGraphVerifiedTruthImportError("FORBIDDEN_PRIVATE_FIELDS", "BusinessGraph verified-truth import rejected forbidden Run/ClientOps private fields");
    }
    const parsed = BusinessGraphVerifiedTruthExportV1Schema.safeParse(payload);
    if (!parsed.success) {
        throw new BusinessGraphVerifiedTruthImportError("INVALID_PAYLOAD", `BusinessGraph verified-truth import payload invalid: ${parsed.error.message}`);
    }
    const merged = {
        ...parsed.data.graph,
        provenance: parsed.data.provenance,
    };
    return parseBusinessGraphWebsiteSafe(merged);
}
/** Parse export envelope without merging back to graph (snapshot validation). */
export function parseBusinessGraphVerifiedTruthExport(payload) {
    if (businessGraphPayloadContainsForbiddenKeys(payload)) {
        throw new BusinessGraphVerifiedTruthImportError("FORBIDDEN_PRIVATE_FIELDS", "BusinessGraph verified-truth export rejected forbidden Run/ClientOps private fields");
    }
    return BusinessGraphVerifiedTruthExportV1Schema.parse(payload);
}
//# sourceMappingURL=business-graph-export-import.js.map